import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { customizationsService } from "../../services/customizations";
import { collectionsService } from "../../services/collections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Plus, Pencil, Trash2,
    Type, List, Image as ImageIcon, X, Search, Check,
    Loader2, Info, ChevronDown, ChevronUp, Package, ChevronRight, Save, Layers
} from "lucide-react";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import productsService from "../../services/products";
import { Card, CardContent } from "@/components/ui/card";
import { getImageUrl } from "../../utils/imageUtils";
import { Badge } from "@/components/ui/badge";

export default function AdminCustomizations() {
    const [customizations, setCustomizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [activeDialogTab, setActiveDialogTab] = useState("form-builder");

    const [formData, setFormData] = useState({
        name: "",
        price: 0,
        is_active: true,
        form_fields: [],
        products: []
    });
    const [allProducts, setAllProducts] = useState([]);
    const [productSearch, setProductSearch] = useState("");

    // ── Products Tab State ──
    const [collections, setCollections] = useState([]);
    const [selectedCollectionId, setSelectedCollectionId] = useState("");
    const [collectionProducts, setCollectionProducts] = useState([]);
    const [loadingCollectionProducts, setLoadingCollectionProducts] = useState(false);
    const [collectionProductSearch, setCollectionProductSearch] = useState("");
    // Map: product_id -> { selected: bool, type: 'mandatory' | 'optional' }
    const [assignments, setAssignments] = useState({});
    const [assignedProducts, setAssignedProducts] = useState([]); // from API
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [savingAssignments, setSavingAssignments] = useState(false);
    const [assignedCollapsed, setAssignedCollapsed] = useState(false);

    // ── Picker State ──
    const [pickerOpen, setPickerOpen] = useState(false);
    const [expandedCollections, setExpandedCollections] = useState(new Set());
    const [collectionProductsMap, setCollectionProductsMap] = useState({});
    const [fetchingPickerProducts, setFetchingPickerProducts] = useState({});
    const [pickerSearch, setPickerSearch] = useState("");

    useEffect(() => {
        fetchData();
        fetchProducts();
    }, []);

    const fetchData = async () => {
        try {
            const data = await customizationsService.getAll();
            setCustomizations(data);
        } catch (error) {
            toast.error("Failed to fetch customizations");
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const data = await productsService.adminProducts.getAll({ limit: 2000, source: 'admin' });
            setAllProducts(data.data || []);
        } catch (error) {
            console.error("Failed to fetch products", error);
        }
    };

    const fetchCollections = async () => {
        try {
            const response = await collectionsService.getAll();
            const data = response.data || response;
            setCollections(data);
        } catch (error) {
            console.error("Failed to fetch collections", error);
        }
    };

    const fetchCollectionProducts = async (collectionId) => {
        if (!collectionId) return;
        setLoadingCollectionProducts(true);
        try {
            const data = await collectionsService.getProducts(collectionId);
            setCollectionProducts(data);
        } catch (error) {
            toast.error("Failed to load collection products");
            setCollectionProducts([]);
        } finally {
            setLoadingCollectionProducts(false);
        }
    };

    const fetchAssignedProducts = async (customizationId) => {
        setLoadingAssigned(true);
        try {
            const data = await customizationsService.getAssignedProducts(customizationId);
            setAssignedProducts(data);

            // Pre-fill assignments map with existing assignments
            // Handle both API formats: assignment_types (array) and assignment_type (string)
            const map = {};
            for (const p of data) {
                let types = [];
                if (Array.isArray(p.assignment_types) && p.assignment_types.length > 0) {
                    types = p.assignment_types;
                } else if (p.assignment_type) {
                    types = [p.assignment_type];
                }
                map[p.product_id] = { selected: true, types };
            }
            setAssignments(map);
        } catch (error) {
            console.error("Failed to fetch assigned products", error);
            setAssignedProducts([]);
        } finally {
            setLoadingAssigned(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: "",
            price: 0,
            is_active: true,
            form_fields: [],
            products: []
        });
        setEditingId(null);
        setProductSearch("");
        setActiveDialogTab("form-builder");
        setSelectedCollectionId("");
        setCollectionProducts([]);
        setCollectionProductSearch("");
        setAssignments({});
        setAssignedProducts([]);
        setAssignedCollapsed(false);
        setExpandedCollections(new Set());
        setCollectionProductsMap({});
        setPickerSearch("");
    };

    const toggleActive = async (custom) => {
        try {
            await customizationsService.update(custom.customization_id, {
                is_active: !custom.is_active
            });
            toast.success(`${custom.name} is now ${!custom.is_active ? 'active' : 'inactive'}`);
            fetchData();
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    const handleOpenDialog = () => {
        resetForm();
        setDialogOpen(true);
    };

    const handleEdit = async (customization) => {
        setEditingId(customization.customization_id);
        setFormData({
            name: customization.name,
            price: customization.price || 0,
            is_active: customization.is_active,
            form_fields: customization.form_fields || [],
            products: customization.products || []
        });
        setProductSearch("");
        setActiveDialogTab("form-builder");
        setSelectedCollectionId("");
        setCollectionProducts([]);
        setCollectionProductSearch("");
        setAssignedCollapsed(false);
        setDialogOpen(true);

        // Fetch collections and assigned products for the Products tab
        fetchCollections();
        fetchAssignedProducts(customization.customization_id);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            await customizationsService.delete(id);
            toast.success("Customization deleted");
            fetchData();
        } catch (error) {
            toast.error("Failed to delete");
        }
    };

    const toggleCollectionExpansion = async (collectionId) => {
        const newExpanded = new Set(expandedCollections);
        if (newExpanded.has(collectionId)) {
            newExpanded.delete(collectionId);
        } else {
            newExpanded.add(collectionId);
            // Fetch products if not already loaded
            if (!collectionProductsMap[collectionId]) {
                setFetchingPickerProducts(prev => ({ ...prev, [collectionId]: true }));
                try {
                    const data = await collectionsService.getProducts(collectionId);
                    setCollectionProductsMap(prev => ({ ...prev, [collectionId]: data }));
                } catch (error) {
                    toast.error("Failed to load products for collection");
                } finally {
                    setFetchingPickerProducts(prev => ({ ...prev, [collectionId]: false }));
                }
            }
        }
        setExpandedCollections(newExpanded);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await customizationsService.update(editingId, formData);
                toast.success("Updated successfully");
            } else {
                await customizationsService.create(formData);
                toast.success("Created successfully");
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error("Failed to save");
        }
    };

    // ── Form Builder Helpers ──
    const addField = (type) => {
        const newField = {
            field_type: type,
            label: `New ${type} field`,
            required: false,
            options: type === 'dropdown' ? [{ label: "Option 1", price_modifier: 0 }] : undefined,
            allowed_file_types: type === 'image' ? ['jpg', 'png'] : undefined,
            max_file_size: type === 'image' ? 5 : undefined
        };
        setFormData({ ...formData, form_fields: [...formData.form_fields, newField] });
    };

    const removeField = (index) => {
        const newFields = [...formData.form_fields];
        newFields.splice(index, 1);
        setFormData({ ...formData, form_fields: newFields });
    };

    const updateField = (index, key, value) => {
        const newFields = [...formData.form_fields];
        newFields[index] = { ...newFields[index], [key]: value };
        setFormData({ ...formData, form_fields: newFields });
    };

    const updateOption = (fieldIndex, optionIndex, key, value) => {
        const newFields = [...formData.form_fields];
        const newOptions = [...newFields[fieldIndex].options];
        newOptions[optionIndex] = { ...newOptions[optionIndex], [key]: value };
        newFields[fieldIndex].options = newOptions;
        setFormData({ ...formData, form_fields: newFields });
    };

    const addOption = (fieldIndex) => {
        const newFields = [...formData.form_fields];
        newFields[fieldIndex].options.push({ label: "New Option", price_modifier: 0 });
        setFormData({ ...formData, form_fields: newFields });
    };

    const removeOption = (fieldIndex, optionIndex) => {
        const newFields = [...formData.form_fields];
        newFields[fieldIndex].options.splice(optionIndex, 1);
        setFormData({ ...formData, form_fields: newFields });
    };

    // ── Products Tab Helpers ──
    const handleCollectionChange = (collectionId) => {
        setSelectedCollectionId(collectionId);
        setCollectionProductSearch("");
        if (collectionId) {
            fetchCollectionProducts(collectionId);
        } else {
            setCollectionProducts([]);
        }
    };

    const toggleProductSelection = (productId) => {
        setAssignments(prev => {
            const existing = prev[productId];
            if (existing?.selected) {
                const copy = { ...prev };
                delete copy[productId];
                return copy;
            } else {
                return { ...prev, [productId]: { selected: true, types: ['optional'] } };
            }
        });
    };

    const toggleAssignmentType = (productId, type) => {
        setAssignments(prev => {
            const current = prev[productId] || { selected: true, types: [] };
            const newTypes = current.types.includes(type)
                ? current.types.filter(t => t !== type)
                : [...current.types, type];

            return {
                ...prev,
                [productId]: { ...current, types: newTypes }
            };
        });
    };

    const toggleCollectionSelection = async (collectionId) => {
        let products = collectionProductsMap[collectionId];

        // If not loaded, fetch them
        if (!products) {
            setFetchingPickerProducts(prev => ({ ...prev, [collectionId]: true }));
            try {
                products = await collectionsService.getProducts(collectionId);
                setCollectionProductsMap(prev => ({ ...prev, [collectionId]: products }));
                // Ensure collection is expanded to show loading/products
                setExpandedCollections(prev => {
                    const newSet = new Set(prev);
                    newSet.add(collectionId);
                    return newSet;
                });
            } catch (error) {
                toast.error("Failed to load products for collection");
                products = [];
            } finally {
                setFetchingPickerProducts(prev => ({ ...prev, [collectionId]: false }));
            }
        }

        if (!products || products.length === 0) return;

        setAssignments(prev => {
            const allSelected = products.every(p => prev[p.product_id]?.selected);
            const copy = { ...prev };
            if (allSelected) {
                // deselect all
                products.forEach(p => {
                    delete copy[p.product_id];
                });
            } else {
                // select all
                products.forEach(p => {
                    if (!copy[p.product_id]) {
                        copy[p.product_id] = { selected: true, types: ['optional'] };
                    }
                });
            }
            return copy;
        });
    };

    const filteredCollectionProducts = useMemo(() => {
        if (!collectionProductSearch) return collectionProducts;
        const search = collectionProductSearch.toLowerCase();
        return collectionProducts.filter(p =>
            p.name.toLowerCase().includes(search) ||
            (p.sku && p.sku.toLowerCase().includes(search))
        );
    }, [collectionProducts, collectionProductSearch]);

    const renderCustomizationTags = (p) => {
        const product_id = p.product_id;
        const currentAssignment = assignments[product_id];

        return (
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
                {/* Pending status for current customization */}
                {currentAssignment?.selected && currentAssignment.types.map(type => (
                    <Badge
                        key={`${product_id}-${type}`}
                        variant="secondary"
                        className={`${type === 'mandatory' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-blue-50 text-blue-600 border-blue-100'} text-[9px] px-1 py-0 h-4 uppercase font-bold`}
                    >
                        {formData.name || 'Current'} ({type === 'mandatory' ? 'Mandatory' : 'Optional'})
                    </Badge>
                ))}

                {/* Already saved assignments (excluding current if already there to avoid dupes) */}
                {p.customizations?.mandatory?.filter(id => id !== editingId).map(id => {
                    const cust = customizations.find(c => c.customization_id === id);
                    if (!cust) return null;
                    return (
                        <Badge key={id} variant="secondary" className="bg-red-50 text-red-600 border-red-100 text-[9px] px-1 py-0 h-4 uppercase font-bold">
                            {cust.name} (Mandatory)
                        </Badge>
                    );
                })}
                {p.customizations?.optional?.filter(id => id !== editingId).map(id => {
                    const cust = customizations.find(c => c.customization_id === id);
                    if (!cust) return null;
                    return (
                        <Badge key={id} variant="secondary" className="bg-blue-50 text-blue-600 border-blue-100 text-[9px] px-1 py-0 h-4 uppercase font-bold">
                            {cust.name} (Optional)
                        </Badge>
                    );
                })}
            </div>
        );
    };

    const selectedCount = useMemo(() => {
        return Object.values(assignments).filter(a => a.selected).length;
    }, [assignments]);

    const handleSelectAll = () => {
        const allSelected = filteredCollectionProducts.every(p => assignments[p.product_id]?.selected);
        if (allSelected) {
            // Deselect all in this collection
            const newAssignments = { ...assignments };
            filteredCollectionProducts.forEach(p => {
                delete newAssignments[p.product_id];
            });
            setAssignments(newAssignments);
        } else {
            // Select all in this collection
            const newAssignments = { ...assignments };
            filteredCollectionProducts.forEach(p => {
                if (!newAssignments[p.product_id]) {
                    newAssignments[p.product_id] = { selected: true, types: ['optional'] };
                }
            });
            setAssignments(newAssignments);
        }
    };

    const handleSaveAssignments = async () => {
        if (!editingId) return;
        setSavingAssignments(true);
        try {
            // Get all unique product IDs from assignments map
            const assignmentList = Object.entries(assignments)
                .map(([product_id, a]) => ({ product_id, types: a.types }));

            await customizationsService.bulkAssign(editingId, assignmentList);
            toast.success(`Assigned to ${assignmentList.length} products`);

            // Sync with formData for the main Submit button
            setFormData(prev => ({
                ...prev,
                products: assignmentList.filter(a => a.types.length > 0).map(a => a.product_id)
            }));

            // Refresh assigned products
            fetchAssignedProducts(editingId);
        } catch (error) {
            toast.error("Failed to save assignments");
        } finally {
            setSavingAssignments(false);
        }
    };

    const handleRemoveAssignment = async (productId) => {
        if (!editingId) return;
        try {
            await customizationsService.removeAssignment(editingId, productId);
            toast.success("Assignment removed");
            // Remove from local state
            setAssignedProducts(prev => prev.filter(p => p.product_id !== productId));
            setAssignments(prev => {
                const copy = { ...prev };
                delete copy[productId];
                return copy;
            });
        } catch (error) {
            toast.error("Failed to remove assignment");
        }
    };

    const filteredCollections = useMemo(() => {
        if (!pickerSearch) return collections;
        const search = pickerSearch.toLowerCase();
        return collections.filter(c => c.name.toLowerCase().includes(search));
    }, [collections, pickerSearch]);

    const filteredPickerProducts = useMemo(() => {
        if (!pickerSearch || pickerSearch.length < 2) return [];
        const search = pickerSearch.toLowerCase();
        return allProducts.filter(p =>
            p.name.toLowerCase().includes(search) ||
            (p.sku && p.sku.toLowerCase().includes(search))
        ).slice(0, 30);
    }, [allProducts, pickerSearch]);

    const productsToAssign = useMemo(() => {
        const list = [];
        for (const [pid, config] of Object.entries(assignments)) {
            const product = allProducts.find(p => String(p.product_id) === String(pid)) ||
                assignedProducts.find(p => String(p.product_id) === String(pid)) ||
                Object.values(collectionProductsMap).flat().find(p => String(p.product_id) === String(pid));

            if (product) {
                list.push({
                    ...product,
                    _assignKey: pid // The exact key used in the assignments map
                });
            }
        }
        return list;
    }, [assignments, allProducts, assignedProducts, collectionProductsMap]);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-customizations">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                        Customizations
                    </h2>
                    <p className="text-slate-500">Manage product customization options</p>
                </div>
                <Button onClick={handleOpenDialog} className="bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Customization
                </Button>
            </div>

            <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
                <SheetContent side="right" className="sm:max-w-[1000px] overflow-hidden flex flex-col p-0">
                    <SheetHeader className="px-6 py-4 border-b flex-shrink-0 bg-slate-50/50">
                        <div className="flex items-center justify-between">
                            <SheetTitle className="text-xl font-bold">
                                {editingId ? "Edit Customization" : "New Customization"}
                                {editingId && formData.name && (
                                    <span className="ml-2 text-indigo-600">
                                        — {formData.name}
                                    </span>
                                )}
                            </SheetTitle>
                        </div>
                    </SheetHeader>

                    {/* Content Wrapper */}
                    <div className="flex-1 flex flex-col overflow-hidden px-6 py-4 space-y-4">


                        {/* Name / Active — always visible */}
                        <div className="flex-shrink-0 space-y-4">
                            <div className="grid grid-cols-1 gap-4">
                                <div className="space-y-2">
                                    <Label>Customization Name</Label>
                                    <Input
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g. Logo Embroidery"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Base Price ($)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                                        placeholder="0.00"
                                    />
                                    <p className="text-[10px] text-slate-400 italic">One-time base fee for this customization.</p>
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <Tabs
                            value={activeDialogTab}
                            onValueChange={setActiveDialogTab}
                            className="flex-1 flex flex-col overflow-hidden mt-2"
                        >
                            <TabsList className="grid grid-cols-2 w-full flex-shrink-0">
                                <TabsTrigger value="form-builder">Form Builder</TabsTrigger>
                                <TabsTrigger value="products" disabled={!editingId}>
                                    <Package className="w-4 h-4 mr-2" />
                                    Products
                                </TabsTrigger>
                            </TabsList>

                            {/* ══════════ FORM BUILDER TAB ══════════ */}
                            <TabsContent value="form-builder" className="flex-1 overflow-y-auto mt-4 pr-1 space-y-6">
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="border-t pt-4">
                                        <h3 className="font-semibold mb-3">Form Builder</h3>
                                        <div className="flex gap-2 mb-4">
                                            <Button type="button" variant="outline" size="sm" onClick={() => addField('text')}>
                                                <Type className="w-4 h-4 mr-2" /> Text
                                            </Button>
                                            <Button type="button" variant="outline" size="sm" onClick={() => addField('dropdown')}>
                                                <List className="w-4 h-4 mr-2" /> Dropdown
                                            </Button>
                                            <Button type="button" variant="outline" size="sm" onClick={() => addField('checkbox')}>
                                                <Check className="w-4 h-4 mr-2" /> Checkbox
                                            </Button>
                                            <Button type="button" variant="outline" size="sm" onClick={() => addField('image')}>
                                                <ImageIcon className="w-4 h-4 mr-2" /> Image
                                            </Button>
                                        </div>

                                        <div className="space-y-4">
                                            {formData.form_fields.length === 0 && (
                                                <p className="text-center text-slate-500 py-4 border border-dashed rounded-lg">
                                                    No fields added. Click buttons above to add fields.
                                                </p>
                                            )}

                                            {formData.form_fields.map((field, idx) => (
                                                <Card key={idx} className="bg-slate-50 border-slate-200">
                                                    <CardContent className="p-4 space-y-3">
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex items-center gap-2">
                                                                <span className="bg-slate-200 text-xs px-2 py-1 rounded uppercase font-bold text-slate-600">
                                                                    {field.field_type}
                                                                </span>
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-red-500 h-8 w-8 p-0"
                                                                onClick={() => removeField(idx)}
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </Button>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div className="space-y-1">
                                                                <Label className="text-xs">Field Label</Label>
                                                                <Input
                                                                    value={field.label}
                                                                    onChange={(e) => updateField(idx, 'label', e.target.value)}
                                                                    placeholder="Enter label"
                                                                    className="bg-white h-8"
                                                                />
                                                            </div>

                                                            <div className="flex items-end pb-2">
                                                                <div className="flex items-center space-x-2">
                                                                    <Switch
                                                                        checked={field.required}
                                                                        onCheckedChange={(checked) => updateField(idx, 'required', checked)}
                                                                    />
                                                                    <Label className="text-xs">Required</Label>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {field.field_type === 'text' && (
                                                            <div className="space-y-1">
                                                                <Label className="text-xs">Placeholder</Label>
                                                                <Input
                                                                    value={field.placeholder || ''}
                                                                    onChange={(e) => updateField(idx, 'placeholder', e.target.value)}
                                                                    className="bg-white h-8"
                                                                />
                                                            </div>
                                                        )}

                                                        {field.field_type === 'dropdown' && (
                                                            <div className="space-y-2">
                                                                <Label className="text-xs">Options</Label>
                                                                {field.options?.map((opt, optIdx) => (
                                                                    <div key={optIdx} className="flex gap-2">
                                                                        <Input
                                                                            value={opt.label}
                                                                            onChange={(e) => updateOption(idx, optIdx, 'label', e.target.value)}
                                                                            placeholder="Option Label"
                                                                            className="bg-white h-8 flex-1"
                                                                        />
                                                                        <Input
                                                                            type="number"
                                                                            value={opt.price_modifier}
                                                                            onChange={(e) => updateOption(idx, optIdx, 'price_modifier', parseFloat(e.target.value))}
                                                                            placeholder="Price +$"
                                                                            className="bg-white h-8 w-24"
                                                                        />
                                                                        <Button
                                                                            type="button"
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => removeOption(idx, optIdx)}
                                                                            className="h-8 w-8 text-red-500"
                                                                        >
                                                                            <X className="w-3 h-3" />
                                                                        </Button>
                                                                    </div>
                                                                ))}
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => addOption(idx)}
                                                                    className="text-xs h-7"
                                                                >
                                                                    Add Option
                                                                </Button>
                                                            </div>
                                                        )}

                                                        {field.field_type === 'image' && (
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <div className="space-y-1">
                                                                    <Label className="text-xs">Max Size (MB)</Label>
                                                                    <Input
                                                                        type="number"
                                                                        value={field.max_file_size || 5}
                                                                        onChange={(e) => updateField(idx, 'max_file_size', parseFloat(e.target.value))}
                                                                        className="bg-white h-8"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>

                                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                                        {editingId ? "Update Customization" : "Create Customization"}
                                    </Button>
                                </form>
                            </TabsContent>

                            {/* ══════════ PRODUCTS TAB ══════════ */}
                            <TabsContent value="products" className="flex-1 overflow-y-auto mt-4 pr-1">
                                <div className="space-y-5">
                                    {/* Info Banner */}
                                    <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                        <p className="text-sm text-blue-700">
                                            Select a collection to bulk-assign this customization to multiple products at once.
                                        </p>
                                    </div>

                                    {/* Searchable Hierarchical Picker */}
                                    <div className="space-y-2">
                                        <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Products to Customize</Label>

                                        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    role="combobox"
                                                    aria-expanded={pickerOpen}
                                                    className="w-full justify-between h-12 bg-white"
                                                >
                                                    <div className="flex items-center gap-2 text-slate-600">
                                                        <Search className="w-4 h-4" />
                                                        <span>{selectedCount > 0 ? `${selectedCount} products selected` : "Search collections and products..."}</span>
                                                    </div>
                                                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-[800px] p-0" align="start">
                                                <Command shouldFilter={false}>
                                                    <CommandInput
                                                        placeholder="Search collections..."
                                                        value={pickerSearch}
                                                        onValueChange={setPickerSearch}
                                                    />
                                                    <CommandList
                                                        className="max-h-[450px] overflow-y-auto overscroll-contain"
                                                        onWheel={(e) => {
                                                            // Prevent the scroll from bubbling up to the Sheet
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <CommandEmpty>No results found.</CommandEmpty>
                                                        <CommandGroup heading={filteredCollections.length > 0 && filteredPickerProducts.length > 0 ? "Collections" : ""}>
                                                            {filteredCollections.map(collection => {
                                                                const collectionId = collection.collection_id;
                                                                const isExpanded = expandedCollections.has(collectionId);
                                                                const products = collectionProductsMap[collectionId] || [];
                                                                const loading = fetchingPickerProducts[collectionId];

                                                                return (
                                                                    <div key={collectionId} className="border-b last:border-0 border-slate-100">
                                                                        <div
                                                                            onClick={() => toggleCollectionExpansion(collectionId)}
                                                                            className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                                                                        >
                                                                            <div className="flex items-center gap-3">
                                                                                <Checkbox
                                                                                    checked={products.length > 0 && products.every(p => assignments[p.product_id]?.selected)}
                                                                                    onCheckedChange={() => toggleCollectionSelection(collectionId)}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                                <div className="w-8 h-8 rounded bg-indigo-50 flex items-center justify-center">
                                                                                    <Layers className="w-4 h-4 text-indigo-600" />
                                                                                </div>
                                                                                <div>
                                                                                    <p className="text-sm font-semibold text-slate-900">{collection.name}</p>
                                                                                    <p className="text-[10px] text-slate-400 capitalize">{collection.type || 'Collection'}</p>
                                                                                </div>
                                                                            </div>
                                                                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                                        </div>

                                                                        {isExpanded && (
                                                                            <div className="bg-slate-50/50 pl-8 pr-2 py-1 space-y-1">
                                                                                {loading ? (
                                                                                    <div className="py-2 text-center text-xs text-slate-400">Loading products...</div>
                                                                                ) : products.length === 0 ? (
                                                                                    <div className="py-2 text-center text-xs text-slate-400">No products in this collection</div>
                                                                                ) : (
                                                                                    products.map(p => {
                                                                                        const isSelected = !!assignments[p.product_id];
                                                                                        return (
                                                                                            <div
                                                                                                key={p.product_id}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    toggleProductSelection(p.product_id);
                                                                                                }}
                                                                                                className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-white'}`}
                                                                                            >
                                                                                                <div className="flex items-center gap-3">
                                                                                                    <Checkbox
                                                                                                        checked={isSelected}
                                                                                                        onCheckedChange={() => toggleProductSelection(p.product_id)}
                                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                                        className="mr-1"
                                                                                                    />
                                                                                                    {p.image_url ? (
                                                                                                        <img src={getImageUrl(p.image_url)} alt="" className="w-8 h-8 rounded border object-cover" />
                                                                                                    ) : (
                                                                                                        <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                                                                                                            <Package className="w-3.5 h-3.5 text-slate-400" />
                                                                                                        </div>
                                                                                                    )}
                                                                                                    <div>
                                                                                                        <div>
                                                                                                            <p className="text-sm font-medium">{p.name}</p>
                                                                                                            {renderCustomizationTags(p)}
                                                                                                        </div>
                                                                                                        {p.sku && <p className="text-[10px] text-slate-400">SKU: {p.sku}</p>}
                                                                                                    </div>
                                                                                                </div>
                                                                                                {isSelected && <Check className="w-4 h-4 text-indigo-600" />}
                                                                                            </div>
                                                                                        );
                                                                                    })
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </CommandGroup>

                                                        {filteredPickerProducts.length > 0 && (
                                                            <>
                                                                {filteredCollections.length > 0 && <div className="mx-2 my-1 border-t border-slate-100" />}
                                                                <CommandGroup heading="Products">
                                                                    {filteredPickerProducts.map(p => {
                                                                        const isSelected = !!assignments[p.product_id];
                                                                        return (
                                                                            <div
                                                                                key={`global-${p.product_id}`}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleProductSelection(p.product_id);
                                                                                }}
                                                                                className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-50'}`}
                                                                            >
                                                                                <div className="flex items-center gap-3">
                                                                                    <Checkbox
                                                                                        checked={isSelected}
                                                                                        onCheckedChange={() => toggleProductSelection(p.product_id)}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        className="mr-1"
                                                                                    />
                                                                                    {p.image_url ? (
                                                                                        <img src={getImageUrl(p.image_url)} alt="" className="w-8 h-8 rounded border object-cover" />
                                                                                    ) : (
                                                                                        <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                                                                                            <Package className="w-3.5 h-3.5 text-slate-400" />
                                                                                        </div>
                                                                                    )}
                                                                                    <div>
                                                                                        <div>
                                                                                            <p className="text-sm font-medium">{p.name}</p>
                                                                                            {renderCustomizationTags(p)}
                                                                                        </div>
                                                                                        {p.sku && <p className="text-[10px] text-slate-400">SKU: {p.sku}</p>}
                                                                                    </div>
                                                                                </div>
                                                                                {isSelected && <Check className="w-4 h-4 text-indigo-600" />}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </CommandGroup>
                                                            </>
                                                        )}
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </div>

                                    {/* Current/Pending Assignments List */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                                Product Assignments ({productsToAssign.length})
                                            </Label>
                                            {productsToAssign.length > 0 && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-xs text-red-500 h-8"
                                                    onClick={() => setAssignments({})}
                                                >
                                                    Clear All
                                                </Button>
                                            )}
                                        </div>

                                        {productsToAssign.length === 0 ? (
                                            <div className="text-center py-10 border rounded-xl border-dashed bg-slate-50/50">
                                                <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                                <p className="text-sm text-slate-500">No products selected yet.</p>
                                                <p className="text-xs text-slate-400">Use the picker above to find and add products.</p>
                                            </div>
                                        ) : (
                                            <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                                                <div className="max-h-[400px] overflow-y-auto divide-y">
                                                    {productsToAssign.map(p => {
                                                        const key = p._assignKey || p.product_id;
                                                        const types = assignments[key]?.types || [];
                                                        return (
                                                            <div key={key} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50">
                                                                <div className="flex items-center gap-3 min-w-0">
                                                                    {p.image_url ? (
                                                                        <img src={getImageUrl(p.image_url)} alt="" className="w-10 h-10 rounded-lg border object-cover flex-shrink-0" />
                                                                    ) : (
                                                                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                                                            <Package className="w-5 h-5 text-slate-400" />
                                                                        </div>
                                                                    )}
                                                                    <div className="min-w-0">
                                                                        <p className="text-sm font-semibold truncate text-slate-900">{p.name}</p>
                                                                        {p.sku && <p className="text-xs text-slate-400">SKU: {p.sku}</p>}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-4 flex-shrink-0">
                                                                    <div style={{ display: 'flex', padding: '4px', backgroundColor: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleAssignmentType(key, 'mandatory')}
                                                                            style={{
                                                                                padding: '6px 12px',
                                                                                borderRadius: '6px',
                                                                                fontSize: '10px',
                                                                                fontWeight: 700,
                                                                                textTransform: 'uppercase',
                                                                                letterSpacing: '0.05em',
                                                                                border: 'none',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s',
                                                                                backgroundColor: types.includes('mandatory') ? '#f43f5e' : 'transparent',
                                                                                color: types.includes('mandatory') ? '#ffffff' : '#94a3b8',
                                                                                boxShadow: types.includes('mandatory') ? '0 4px 6px -1px rgba(244, 63, 94, 0.3)' : 'none',
                                                                            }}
                                                                        >
                                                                            Mandatory
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleAssignmentType(key, 'optional')}
                                                                            style={{
                                                                                padding: '6px 12px',
                                                                                borderRadius: '6px',
                                                                                fontSize: '10px',
                                                                                fontWeight: 700,
                                                                                textTransform: 'uppercase',
                                                                                letterSpacing: '0.05em',
                                                                                border: 'none',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s',
                                                                                backgroundColor: types.includes('optional') ? '#4f46e5' : 'transparent',
                                                                                color: types.includes('optional') ? '#ffffff' : '#94a3b8',
                                                                                boxShadow: types.includes('optional') ? '0 4px 6px -1px rgba(79, 70, 229, 0.3)' : 'none',
                                                                            }}
                                                                        >
                                                                            Optional
                                                                        </button>
                                                                    </div>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="w-8 h-8 p-0 text-slate-400 hover:text-red-500"
                                                                        onClick={() => toggleProductSelection(key)}
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Save Button */}
                                    {productsToAssign.length > 0 && (
                                        <div className="pt-4 border-t flex justify-end">
                                            <Button
                                                type="button"
                                                onClick={handleSaveAssignments}
                                                disabled={savingAssignments}
                                                className="bg-indigo-600 hover:bg-indigo-700 min-w-[150px]"
                                            >
                                                {savingAssignments ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Save className="w-4 h-4 mr-2" />
                                                )}
                                                Save Assignments
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                </SheetContent>
            </Sheet>

            <div className="card-elevated overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead>Name</TableHead>
                            <TableHead>Fields</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {customizations.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                                    No customizations found. Create one to get started.
                                </TableCell>
                            </TableRow>
                        ) : (
                            customizations.map((custom) => (
                                <TableRow key={custom.customization_id}>
                                    <TableCell className="font-medium">{custom.name}</TableCell>
                                    <TableCell>
                                        <div className="flex gap-1 flex-wrap">
                                            {custom.form_fields?.map((f, i) => (
                                                <span key={i} className="bg-slate-100 text-xs px-2 py-1 rounded border border-slate-200">
                                                    {f.field_type}
                                                </span>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={custom.is_active}
                                                onCheckedChange={() => toggleActive(custom)}
                                            />
                                            <span className={`text-xs font-medium ${custom.is_active ? 'text-indigo-600' : 'text-slate-400'}`}>
                                                {custom.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => handleEdit(custom)}>
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(custom.customization_id)} className="text-red-500">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
